'use client';

import { c as _c } from "react/compiler-runtime";
import AuthWidgetContext from '@shared/utils/context/AuthWidgetContext';
import useUser from '@shared/utils/hooks/useUser';
import dynamic from 'next/dynamic';
import { type ComponentProps, useContext } from 'react';
import type { PostInput as PostInputType } from './PostInput';
type Props = ComponentProps<typeof PostInputType>;

// The rich-text editor (TipTap + ProseMirror + tiptap-markdown) is ~340 KB
// brotli. Importing PostInput statically put it in the chunk every anonymous
// visitor downloads, because composers and edit forms sit inside the shared
// post components. Loading it here, and only for a signed-in user, keeps it off
// logged-out pages entirely; nobody signed out can post anyway.
const Editor = dynamic(() => import('./PostInput').then(m => m.PostInput), {
  ssr: false,
  loading: () => null
});

// Same floor as the editor's own min-height, so swapping it in doesn't shift.
const minHeight = (rows?: number) => `${Math.max(rows ?? 4, 4) * 26}px`;
export const PostInput = props => {
  const $ = _c(19);
  const {
    currentUser
  } = useUser();
  const {
    openAuthContinueModal
  } = useContext(AuthWidgetContext);
  if (!currentUser.isAuthenticated) {
    let t0;
    if ($[0] !== openAuthContinueModal) {
      t0 = () => openAuthContinueModal();
      $[0] = openAuthContinueModal;
      $[1] = t0;
    } else {
      t0 = $[1];
    }
    let t1;
    if ($[2] !== props.rows) {
      t1 = minHeight(props.rows);
      $[2] = props.rows;
      $[3] = t1;
    } else {
      t1 = $[3];
    }
    let t2;
    if ($[4] !== t1) {
      t2 = {
        minHeight: t1
      };
      $[4] = t1;
      $[5] = t2;
    } else {
      t2 = $[5];
    }
    let t3;
    if ($[6] !== props.label || $[7] !== t0 || $[8] !== t2) {
      t3 = <button type="button" onClick={t0} className="w-full px-3 py-2 text-left text-base text-textSecondary" style={t2}>{props.label}</button>;
      $[6] = props.label;
      $[7] = t0;
      $[8] = t2;
      $[9] = t3;
    } else {
      t3 = $[9];
    }
    return t3;
  }
  let t0;
  if ($[10] !== props.rows) {
    t0 = minHeight(props.rows);
    $[10] = props.rows;
    $[11] = t0;
  } else {
    t0 = $[11];
  }
  let t1;
  if ($[12] !== t0) {
    t1 = {
      minHeight: t0
    };
    $[12] = t0;
    $[13] = t1;
  } else {
    t1 = $[13];
  }
  let t2;
  if ($[14] !== props) {
    t2 = <Editor {...props} />;
    $[14] = props;
    $[15] = t2;
  } else {
    t2 = $[15];
  }
  let t3;
  if ($[16] !== t1 || $[17] !== t2) {
    t3 = <div style={t1}>{t2}</div>;
    $[16] = t1;
    $[17] = t2;
    $[18] = t3;
  } else {
    t3 = $[18];
  }
  return t3;
};